import { useEffect, useRef } from 'react'

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  const touchStartX = useRef(null)
  const touchEndX = useRef(null)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') onPrev()
      if (e.key === 'ArrowRight') onNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'auto'
    }
  }, [onClose, onPrev, onNext])

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return
    const diff = touchStartX.current - touchEndX.current
    if (diff > 50) {
      onNext()
    } else if (diff < -50) {
      onPrev()
    }
    touchStartX.current = null
    touchEndX.current = null
  }

  if (currentIndex === null || !images || images.length === 0) return null

  const currentImg = images[currentIndex]

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md"
      onClick={onClose}
    >
      {/* Top Bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-4 z-10 bg-gradient-to-b from-black/80 to-transparent">
        <span className="text-sm font-medium text-neutral-400">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          className="p-2.5 rounded-full bg-neutral-900/80 text-white hover:bg-neutral-800 transition-colors cursor-pointer"
          aria-label="Luk galleri"
        >
          <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
        </button>
      </div>

      {/* Prev Button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3.5 rounded-full bg-neutral-900/80 text-white hover:bg-gold-400 hover:text-black transition-colors cursor-pointer hidden sm:flex items-center justify-center shadow-lg"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Image container with touch handlers */}
      <div
        className="relative max-w-5xl max-h-[85vh] w-full p-4 flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentImg}
          alt={`Casper DJ arrangement ${currentIndex + 1}`}
          className="max-h-[82vh] max-w-full object-contain rounded-lg shadow-2xl transition-opacity duration-300"
          loading="eager"
        />
      </div>

      {/* Next Button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3.5 rounded-full bg-neutral-900/80 text-white hover:bg-gold-400 hover:text-black transition-colors cursor-pointer hidden sm:flex items-center justify-center shadow-lg"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Mobile touch hint */}
      <div className="absolute bottom-4 inset-x-0 text-center text-xs text-neutral-500 sm:hidden pointer-events-none">
        Swipe for at skifte billede
      </div>
    </div>
  )
}
